/**
 * Shared Highcharts theme configuration for all chart components
 * Single source of truth for ChartWidget, ChartBlock, and other widgets
 */

/**
 * Comprehensive theme for ChartWidget and ChartBlock
 * Includes crosshair labels, exporting, navigator, dateTimeLabelFormats
 */
export const getThemeConfig = (isDarkMode) => ({
    chart: {
        backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
        height: 300,
        style: {
            fontFamily: 'Inter, system-ui, sans-serif',
        },
    },
    title: {
        style: {
            color: isDarkMode ? '#e5e5e5' : '#333333',
            fontSize: '16px',
            fontWeight: '600',
        },
    },
    xAxis: {
        labels: {
            style: {
                color: isDarkMode ? '#e5e5e5' : '#333333',
            },
        },
        gridLineColor: isDarkMode ? '#282828' : '#f0f0f0',
        gridLineWidth: 1,
        lineWidth: 0,
        tickLength: 0,
        dateTimeLabelFormats: {
            millisecond: '%H:%M:%S.%L',
            second: '%H:%M:%S',
            minute: '%H:%M',
            hour: '%H:%M',
            day: '%e %b',
            week: '%e %b',
            month: "%b '%y",
            year: '%Y',
        },
        crosshair: {
            width: 1,
            color: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
            dashStyle: 'Solid',
            label: {
                enabled: true,
                format: '{value:%e %b %Y}',
                backgroundColor: isDarkMode ? '#2a2a2a' : '#f5f5f5',
                style: {
                    color: isDarkMode ? '#e5e5e5' : '#333333',
                    fontSize: '11px',
                    fontWeight: '500',
                },
                padding: 6,
                borderRadius: 4,
            },
        },
    },
    yAxis: {
        labels: {
            style: {
                color: isDarkMode ? '#9ca3af' : '#6b7280',
                fontSize: '11px',
            },
        },
        gridLineWidth: 0,
        lineWidth: 0,
        lineColor: isDarkMode ? '#282828' : '#e5e7eb',
        tickLength: 0,
        showLastLabel: true,
        showFirstLabel: true,
        crosshair: {
            width: 1,
            color: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
            dashStyle: 'Solid',
            label: {
                enabled: true,
                backgroundColor: isDarkMode ? '#2a2a2a' : '#f5f5f5',
                style: {
                    color: isDarkMode ? '#e5e5e5' : '#333333',
                    fontSize: '11px',
                    fontWeight: '500',
                },
                padding: 6,
                borderRadius: 4,
            },
        },
    },
    navigator: {
        enabled: false,
        height: 40,
        margin: 10,
        maskFill: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
        outlineWidth: 0,
        handles: {
            backgroundColor: isDarkMode ? '#374151' : '#d1d5db',
            borderColor: isDarkMode ? '#4b5563' : '#9ca3af',
            width: 8,
            height: 20,
        },
        xAxis: {
            gridLineWidth: 0,
            labels: { enabled: false },
        },
        series: {
            lineWidth: 1,
            color: isDarkMode ? '#6b7280' : '#9ca3af',
            fillOpacity: 0.05,
        },
    },
    rangeSelector: {
        enabled: false,
    },
    scrollbar: {
        enabled: false,
    },
    legend: {
        itemStyle: {
            color: isDarkMode ? '#e5e5e5' : '#333333',
        },
    },
    plotOptions: {
        series: {
            lineWidth: 1.5,
        },
        candlestick: {
            color: '#F23545',
            upColor: '#0A9981',
            lineColor: '#F23545',
            upLineColor: '#0A9981',
        },
        ohlc: {
            color: '#F23545',
            upColor: '#0A9981',
        },
        hollowcandlestick: {
            color: '#F23545',
            upColor: '#0A9981',
            lineColor: '#F23545',
            upLineColor: '#0A9981',
        },
        heikinashi: {
            color: '#F23545',
            upColor: '#0A9981',
            lineColor: '#F23545',
            upLineColor: '#0A9981',
        },
    },
    credits: {
        enabled: false,
    },
    tooltip: {
        shared: true,
        split: false,
        backgroundColor: isDarkMode ? '#1a1d2e' : '#ffffff',
        style: {
            color: isDarkMode ? 'rgba(229, 229, 229, 0.7)' : 'rgba(51, 51, 51, 0.7)',
            fontSize: '13px',
            lineHeight: '20px',
        },
        borderWidth: 0,
        borderRadius: 8,
        shadow: {
            color: 'rgba(0, 0, 0, 0.15)',
            offsetX: 0,
            offsetY: 1,
            opacity: 0.15,
            width: 6,
        },
        padding: 14,
        useHTML: false,
        xDateFormat: '%e %b %Y',
        headerFormat: '<span style="font-size: 13px">{point.key}</span><br/>',
    },
    exporting: {
        enabled: true,
        buttons: {
            contextButton: {
                menuItems: [
                    'downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadSVG',
                    'separator', 'downloadCSV', 'downloadXLS', 'viewData',
                ],
                theme: {
                    fill: isDarkMode ? '#2a2a2a' : '#ffffff',
                    stroke: isDarkMode ? '#444' : '#ccc',
                    states: {
                        hover: {
                            fill: isDarkMode ? '#3a3a3a' : '#f0f0f0',
                        },
                    },
                },
                symbolStroke: isDarkMode ? '#e5e5e5' : '#333333',
            },
        },
        chartOptions: {
            chart: {
                backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
            },
            title: {
                style: { color: isDarkMode ? '#e5e5e5' : '#333333' },
            },
            xAxis: {
                labels: {
                    style: { color: isDarkMode ? '#e5e5e5' : '#333333' },
                },
            },
            yAxis: {
                labels: {
                    style: { color: isDarkMode ? '#9ca3af' : '#6b7280' },
                },
            },
        },
        csv: {
            dateFormat: '%Y-%m-%d %H:%M:%S',
        },
    },
});

/**
 * Simplified theme for lightweight widgets (FinancialChart, PeerComparison, etc.)
 */
export const getChartTheme = (isDarkMode) => ({
    chart: {
        backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
        style: {
            fontFamily: 'Inter, system-ui, sans-serif',
        },
        spacing: [10, 10, 10, 10],
    },
    title: {
        style: {
            color: isDarkMode ? '#e5e5e5' : '#333333',
            fontSize: '16px',
            fontWeight: '600',
        },
    },
    xAxis: {
        labels: {
            style: {
                color: isDarkMode ? '#e5e5e5' : '#333333',
                fontSize: '11px',
            },
        },
        gridLineColor: isDarkMode ? '#282828' : '#f0f0f0',
        gridLineWidth: 1,
        lineWidth: 0,
        tickLength: 0,
        crosshair: {
            width: 1,
            color: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
            dashStyle: 'Solid',
        },
    },
    yAxis: {
        labels: {
            style: {
                color: isDarkMode ? '#e5e5e5' : '#333333',
                fontSize: '11px',
            },
        },
        gridLineWidth: 0,
        lineWidth: 0,
        tickLength: 0,
        title: { text: null },
    },
    legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
            color: isDarkMode ? '#e5e5e5' : '#333333',
            fontWeight: 'normal',
            fontSize: '12px',
        },
        itemHoverStyle: {
            color: isDarkMode ? '#ffffff' : '#000000',
        },
    },
    tooltip: {
        shared: true,
        split: false,
        backgroundColor: isDarkMode ? '#1a1d2e' : '#ffffff',
        style: {
            color: isDarkMode ? 'rgba(229, 229, 229, 0.9)' : 'rgba(51, 51, 51, 0.9)',
            fontSize: '13px',
            lineHeight: '20px',
        },
        borderWidth: 0,
        borderRadius: 8,
        shadow: {
            color: 'rgba(0, 0, 0, 0.15)',
            offsetX: 0,
            offsetY: 1,
            opacity: 0.15,
            width: 6,
        },
        padding: 14,
        useHTML: true,
    },
    plotOptions: {
        series: {
            lineWidth: 1.5,
        },
        column: {
            borderRadius: 4,
            borderWidth: 0,
            groupPadding: 0.2,
        },
        line: {
            lineWidth: 2,
            marker: { enabled: true, radius: 4 },
        },
        area: {
            lineWidth: 2,
            marker: { enabled: false },
        },
        spline: {
            lineWidth: 2,
            marker: { enabled: true, radius: 4 },
        },
        candlestick: {
            color: '#F23545',
            upColor: '#0A9981',
            lineColor: '#F23545',
            upLineColor: '#0A9981',
        },
    },
    credits: { enabled: false },
});

/**
 * Chart colors palette matching ChartBlock
 */
export const CHART_COLORS = [
    '#2563eb', // blue
    '#dc2626', // red
    '#16a34a', // green
    '#9333ea', // purple
    '#ea580c', // orange
    '#0891b2', // cyan
    '#84cc16', // lime
    '#f59e0b', // amber
];

/**
 * Stock colors (green/red for up/down)
 */
export const STOCK_COLORS = {
    up: '#0A9981',
    down: '#F23545',
    neutral: '#9ca3af',
};

/**
 * Get candlestick/volume color based on price direction
 */
export const getPriceColor = (isUp, opacity = 1) => {
    const color = isUp ? STOCK_COLORS.up : STOCK_COLORS.down;
    if (opacity < 1) {
        // Convert hex to rgba
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    return color;
};

/**
 * Create tooltip formatter for financial data
 * @param {Object} indicatorFormats - Map of indicator name to format type
 * @param {Function} formatValue - Function to format values
 */
export const createTooltipFormatter = (indicatorFormats, formatValue) => {
    return function() {
        let html = `<div style="padding: 2px 0"><div style="font-weight: 500; margin-bottom: 8px">${this.x}</div>`;
        this.points.forEach(point => {
            const seriesName = point.series.name;
            const indicatorName = seriesName.includes('(') ? seriesName.split(' (')[0] : seriesName;
            const format = indicatorFormats[indicatorName] || 'number';
            html += `
                <div style="display: flex; align-items: center; gap: 8px; font-size: 12px; margin: 4px 0">
                    <span style="color:${point.color}">●</span>
                    <span style="opacity: 0.7">${point.series.name}:</span>
                    <span style="font-weight: 500">${formatValue(point.y, format)}</span>
                </div>
            `;
        });
        html += '</div>';
        return html;
    };
};

/**
 * Detect dark mode from document
 */
export const detectDarkMode = () => {
    return document.documentElement.classList.contains('dark');
};

/**
 * Hook to observe dark mode changes
 * @param {Function} callback - Called when dark mode changes
 * @returns {Function} Cleanup function
 */
export const observeDarkMode = (callback) => {
    const observer = new MutationObserver(() => {
        callback(detectDarkMode());
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
};

/**
 * Widget container background color
 */
export const WIDGET_BACKGROUND = {
    light: '#ffffff',
    dark: '#1f2937', // gray-800
};

/**
 * Get widget container class names for Tailwind
 * @param {boolean} isDarkMode - Whether dark mode is enabled
 * @returns {string} Tailwind class names
 */
export const getWidgetContainerClasses = (isDarkMode) => {
    return `rounded-xl overflow-hidden ${isDarkMode ? 'bg-[#1a1a1a]' : 'bg-[#F7F8FA]'}`;
};

export const NON_STOCK_TYPES = [
    'pie', 'polar', 'radar', 'bar', 'column', 'scatter', 'bubble',
    'heatmap', 'treemap', 'wordcloud', 'gauge', 'solidgauge',
    'funnel', 'funnel3d', 'pyramid', 'pyramid3d',
    'sankey', 'dependencywheel', 'networkgraph', 'organization',
    'sunburst', 'venn', 'timeline', 'xrange', 'bullet',
    'variablepie', 'item', 'histogram', 'bellcurve', 'pareto',
    'streamgraph', 'tilemap', 'variwide', 'vector', 'windbarb',
    'arcdiagram', 'packedbubble', 'waterfall', 'boxplot',
    'errorbar', 'lollipop', 'dumbbell',
];

export const VIETNAMESE_LOCALE = {
    months: ['Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4', 'Tháng 5', 'Tháng 6', 'Tháng 7', 'Tháng 8', 'Tháng 9', 'Tháng 10', 'Tháng 11', 'Tháng 12'],
    shortMonths: ['T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10', 'T11', 'T12'],
    weekdays: ['Chủ Nhật', 'Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy'],
};

export const TICKER_INDICES = [
    'VNINDEX', 'VN-INDEX', 'VN30', 'VN30-INDEX',
    'HNX', 'HNX-INDEX', 'HNX30', 'HNX30-INDEX',
    'UPCOM', 'UPCOM-INDEX',
    'VN100', 'VN100-INDEX',
    'VNMID', 'VNSML', 'VNALL', 'VNXALL',
    'VNDIAMOND', 'VNFINLEAD', 'VNFINSELECT',
    'VNX50',
];

export function getTickerUrl(ticker) {
    const tickerUpper = ticker.toUpperCase();
    if (TICKER_INDICES.includes(tickerUpper) || tickerUpper.includes('INDEX') || ticker.includes('-')) {
        return `https://simplize.vn/chart?ticker=${ticker}`;
    }
    return `https://simplize.vn/co-phieu/${ticker}`;
}
